import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AccountsService } from '../accounts.service';
import { AuthCodeInputComponent } from '../code-input';
import { formatCountdown, PasswordlessAuthService, startOtpCountdowns } from '../passwordless';
import { AuthShellComponent } from '../shell';
import { WhatsappLoginFormInterface, WhatsappLoginFormValuesInterface } from './whatsapp-login.interface';

/**
 * Login with WhatsApp OTP for a phone already linked to a confirmed account:
 * step 1 asks for the phone, step 2 is the two-factor style code entry.
 */
@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent, AuthCodeInputComponent],
  selector: 'app-whatsapp-login',
  templateUrl: './whatsapp-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhatsappLoginComponent extends BaseService implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly accountsService = inject(AccountsService);
  private readonly passwordlessAuthService = inject(PasswordlessAuthService);

  formWhatsapp: FormGroup;
  protected readonly remainingSeconds = signal<number>(0);
  protected readonly validitySeconds = signal<number>(0);
  protected readonly hasRequestedCode = signal<boolean>(false);
  protected readonly formatCountdown = formatCountdown;
  private stopCooldown: (() => void) | null = null;

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.stopCooldown?.();
  }

  createForm(): void {
    this.formWhatsapp = new FormGroup<WhatsappLoginFormInterface>({
      phone: new FormControl('', [RxwebValidators.required(), RxwebValidators.minLength({ value: 10 })]),
      code: new FormControl('', [RxwebValidators.required(), RxwebValidators.minLength({ value: 6 }), RxwebValidators.maxLength({ value: 6 })]),
    });
  }

  async requestCode(): Promise<void> {
    if (this.remainingSeconds() > 0) {
      return;
    }
    const phoneControl = this.formWhatsapp.get('phone');
    if (phoneControl?.invalid) {
      return;
    }
    const formValues: WhatsappLoginFormValuesInterface = this.formWhatsapp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .requestWhatsappOtp(formValues.phone)
      .then(() => {
        this.formWhatsapp.get('code')?.reset('');
        this.hasRequestedCode.set(true);
        this.beginCooldown();
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  async verifyCode(): Promise<void> {
    const formValues: WhatsappLoginFormValuesInterface = this.formWhatsapp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .verifyWhatsappOtp(formValues.phone, formValues.code)
      .then(async (): Promise<void> => {
        await this.accountsService.loadAccountIds();
        this._toast.success({ message: 'Login realizado com sucesso!' });
        void this.router.navigateByUrl('/dashboard');
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  protected changePhone(): void {
    this.formWhatsapp.get('code')?.reset('');
    this.hasRequestedCode.set(false);
  }

  private beginCooldown(): void {
    this.stopCooldown?.();
    this.stopCooldown = startOtpCountdowns((state): void => {
      this.remainingSeconds.set(state.resendSeconds);
      this.validitySeconds.set(state.validitySeconds);
    });
  }
}
